import React, { useState } from 'react';
import { RsvpForm } from '@/src/modules/rsvp';
import { registry } from '../registry';
import type { InvitationData, TemplateMetadata } from '../types';

const META: TemplateMetadata = {
  id: 'tpl_baby_shower_dreamy_moon_009',
  name: 'Dreamy Moon Gold',
  occasionType: 'BABY_SHOWER',
  description: 'A dreamy premium baby shower invitation using an ivory background with champagne gold gradients, featuring a sleeping teddy bear on a crescent moon, clouds, and white roses.',
  isPremium: true,
  tags: ['baby-shower', 'premium', 'dreamy', 'moon', 'gold', 'watercolor'],
  colorScheme: { primary: '#78350f', accent: '#ca8a04', bg: '#fffbeb' },
};

export function DreamyMoonGold({ data }: { data: InvitationData }) {
  const baby = data.customData?.baby ?? {};
  const invitationId = data.invitationId ?? data.id ?? null;
  const isLive = data.status === 'PUBLISHED' || data.status === 'PROCESSING';
  const [showRsvpModal, setShowRsvpModal] = useState(false);

  // Read dynamic variables with default fallbacks
  const motherName = baby.motherName || 'Olivia Rose';
  const hostedBy = baby.hostedBy || data.hostName || 'Sarah & David Rose';
  
  // Format Date and Day
  const rawDate = baby.eventDate || data.eventDate || new Date().toISOString();
  let formattedDate = 'DECEMBER 20, 2026';
  let dayOfWeek = 'SUNDAY';
  try {
    const d = new Date(rawDate);
    formattedDate = d.toLocaleDateString('en-US', { day: 'numeric', month: 'long', year: 'numeric' }).toUpperCase();
    dayOfWeek = d.toLocaleDateString('en-US', { weekday: 'long' }).toUpperCase();
  } catch (e) {}

  const eventTime = baby.eventTime || data.eventTime || '03:00 PM';
  const venueName = baby.venueName || data.eventVenue?.split(',')[0] || 'THE GRAND GOLD HALL';
  const venueAddress = baby.venueAddress || data.eventVenue || '101 Champagne Plaza, Beverly Hills, CA';
  const rsvpName = baby.rsvpName || 'Jessica';
  const rsvpPhone = baby.rsvpPhone || '+1 (555) 019-2834';
  const message = baby.message || 'Join us for a magical evening celebrating the upcoming arrival of our little one.';
  const dressCode = baby.dressCode || 'Champagne gold, ivory, or white';

  return (
    <div className="min-h-screen bg-stone-100 py-8 px-4 flex flex-col items-center justify-center font-sans select-none">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Playfair+Display:ital,wght@0,400;0,600;1,400&family=Montserrat:wght@300;400;500;600;700&family=Pinyon+Script&display=swap');
        .font-serif-dreamy { font-family: 'Playfair Display', Georgia, serif; }
        .font-sans-dreamy { font-family: 'Montserrat', sans-serif; }
        .font-cursive-dreamy { font-family: 'Pinyon Script', cursive; }
        .font-title-dreamy { font-family: 'Cinzel', serif; }
        
        .dreamy-bg-ivory {
          background: radial-gradient(circle at 50% 50%, #ffffff 0%, #fffdf9 40%, #faf6ec 70%, #f3ebd9 100%);
          position: relative;
        }
        
        /* Shimmer Overlay */
        .gold-shimmer {
          position: absolute;
          inset: 0;
          pointer-events: none;
          background: linear-gradient(135deg, rgba(255,255,255,0.4) 0%, rgba(251,191,36,0.1) 30%, rgba(255,255,255,0) 60%, rgba(255,255,255,0.3) 100%);
          mix-blend-mode: overlay;
          z-index: 2;
        }

        /* Fine Paper Grain Texture */
        .paper-grain-dreamy {
          position: absolute;
          inset: 0;
          pointer-events: none;
          mix-blend-mode: multiply;
          opacity: 0.16;
          z-index: 1;
        }

        .gold-foil-text {
          background: linear-gradient(135deg, #a16207 0%, #ca8a04 30%, #eab308 50%, #ca8a04 70%, #a16207 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          filter: drop-shadow(0px 1px 1px rgba(255,255,255,0.9));
        }

        .sparkle-pt {
          position: absolute;
          width: 5px;
          height: 5px;
          background-color: rgba(234, 179, 8, 0.7);
          clip-path: polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%);
          animation: twinkleSparkle 4s infinite ease-in-out;
        }
        @keyframes twinkleSparkle {
          0%, 100% { opacity: 0.2; transform: scale(0.8); }
          50% { opacity: 1; transform: scale(1.2); }
        }
      `}</style>

      {/* Main Card Frame */}
      <div className="relative w-full max-w-[540px] aspect-[1080/1920] shadow-2xl rounded-2xl overflow-hidden dreamy-bg-ivory flex flex-col p-10 border-[5px] border-white/85">
        {/* Shimmer & Paper Texture Layers */}
        <div className="gold-shimmer" />
        <svg className="paper-grain-dreamy" width="100%" height="100%">
          <filter id="linenNoise">
            <feTurbulence type="fractalNoise" baseFrequency="0.75" numOctaves="3" result="noise" />
            <feColorMatrix type="matrix" values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 0.05 0" />
            <feComposite operator="in" in2="SourceGraphic" />
          </filter>
          <rect width="100%" height="100%" filter="url(#linenNoise)" />
        </svg>

        {/* Scattered Sparkles */}
        <div className="sparkle-pt top-[18%] left-[15%]" style={{ animationDelay: '0s' }} />
        <div className="sparkle-pt top-[28%] right-[20%]" style={{ animationDelay: '1.5s' }} />
        <div className="sparkle-pt top-[65%] left-[12%]" style={{ animationDelay: '3s' }} />
        <div className="sparkle-pt top-[82%] right-[14%]" style={{ animationDelay: '4.5s' }} />

        {/* Thin Gold Geometric Borders */}
        <div className="absolute inset-4 border border-dashed border-[#ca8a04]/20 rounded-2xl pointer-events-none z-10" />
        <div className="absolute inset-5 border-2 border-[#ca8a04]/35 rounded-2xl pointer-events-none z-10">
          <div className="absolute top-[-2px] left-[-2px] w-5 h-5 border-t border-l border-[#ca8a04]" />
          <div className="absolute top-[-2px] right-[-2px] w-5 h-5 border-t border-r border-[#ca8a04]" />
          <div className="absolute bottom-[-2px] left-[-2px] w-5 h-5 border-b border-l border-[#ca8a04]" />
          <div className="absolute bottom-[-2px] right-[-2px] w-5 h-5 border-b border-r border-[#ca8a04]" />
        </div>

        {/* Watercolor Teddy Bear Sleeping on Crescent Moon with Hanging Stars & Clouds */}
        <div className="absolute top-8 inset-x-0 h-40 pointer-events-none z-10 flex justify-center">
          <svg viewBox="0 0 160 120" className="w-36 h-full">
            <defs>
              <linearGradient id="goldGradDreamy" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#a16207" />
                <stop offset="35%" stopColor="#ca8a04" />
                <stop offset="70%" stopColor="#fef08a" />
                <stop offset="100%" stopColor="#ca8a04" />
              </linearGradient>
            </defs>
            <circle cx="85" cy="55" r="28" fill="#fef08a" opacity="0.15" filter="blur(4px)" />
            <path d="M 95 30 A 24 24 0 1 0 95 78 A 20 20 0 1 1 95 30 Z" fill="url(#goldGradDreamy)" opacity="0.85" />
            <path d="M 76 40 C 65 40, 58 48, 58 58 C 58 68, 68 76, 78 76 C 88 76, 92 68, 92 58 Z" fill="#78350f" opacity="0.3" />
            <circle cx="84" cy="46" r="6" fill="#78350f" opacity="0.32" />
            <circle cx="86" cy="42" r="2" fill="#78350f" opacity="0.32" />
            <path d="M 50 82 Q 62 72, 74 82 Q 86 72, 98 82 Q 110 72, 120 85 Q 85 95, 50 82 Z" fill="#fff" opacity="0.95" />
            <line x1="72" y1="76" x2="72" y2="92" stroke="#eab308" strokeWidth="0.5" />
            <polygon points="72,92 73,94 75,94 74,95 75,97 72,96" fill="#eab308" />
          </svg>
        </div>

        {/* White Roses, Baby's Breath & Gold Botanical Branches Frame */}
        <div className="absolute top-[-5px] left-[-5px] w-28 h-28 pointer-events-none z-10 opacity-90">
          <svg viewBox="0 0 100 100" className="w-full h-full">
            <circle cx="22" cy="22" r="14" fill="#fff" stroke="#fef08a" strokeWidth="0.75" />
            <circle cx="22" cy="22" r="10" fill="#fefaf0" />
            <circle cx="32" cy="18" r="9" fill="#fff" />
            <path d="M 28 8 Q 42 5, 48 18" stroke="#ca8a04" strokeWidth="0.75" fill="none" />
          </svg>
        </div>

        <div className="absolute bottom-[-10px] right-[-10px] w-32 h-32 pointer-events-none z-10 opacity-90">
          <svg viewBox="0 0 100 100" className="w-full h-full transform rotate-180">
            <circle cx="22" cy="22" r="14" fill="#fff" stroke="#fef08a" strokeWidth="0.75" />
            <circle cx="22" cy="22" r="10" fill="#fefaf0" />
            <circle cx="32" cy="18" r="9" fill="#fff" />
            <path d="M 28 8 Q 42 5, 48 18" stroke="#ca8a04" strokeWidth="0.75" fill="none" />
          </svg>
        </div>

        {/* Typography — centered luxury stationery layout */}
        <div className="relative z-20 flex flex-col justify-between h-full pt-48 pb-12 px-4">
          
          {/* Header section */}
          <div className="text-center">
            <p className="font-title-dreamy text-[8.5px] tracking-[0.48em] text-[#78350f]/60 uppercase mb-8">
              JOIN US TO CELEBRATE A
            </p>

            <h2 className="font-title-dreamy text-[4rem] text-[#78350f] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-1 gold-foil-text">
              BABY
            </h2>
            <h2 className="font-title-dreamy text-[4rem] text-[#78350f] tracking-[0.14em] font-semibold leading-[1.05] uppercase mb-9 gold-foil-text">
              SHOWER
            </h2>

            <svg viewBox="0 0 220 12" className="w-44 h-3 mx-auto mb-8" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#78350f" strokeWidth="0.9" opacity="0.45"/>
              <circle cx="110" cy="6" r="2.8" fill="#78350f" opacity="0.55"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#78350f" strokeWidth="0.9" opacity="0.45"/>
            </svg>

            <span className="font-title-dreamy text-[8.5px] text-[#ca8a04]/80 tracking-[0.42em] block mb-8 uppercase">
              IN HONOR OF
            </span>

            <h1 className="font-cursive-dreamy text-[5.8rem] text-[#78350f] leading-none mb-9 gold-foil-text">
              {motherName}
            </h1>

            <svg viewBox="0 0 220 12" className="w-36 h-3 mx-auto mb-5" fill="none">
              <line x1="0" y1="6" x2="88" y2="6" stroke="#78350f" strokeWidth="0.8" opacity="0.40"/>
              <circle cx="110" cy="6" r="2.2" fill="#78350f" opacity="0.48"/>
              <line x1="132" y1="6" x2="220" y2="6" stroke="#78350f" strokeWidth="0.8" opacity="0.40"/>
            </svg>
          </div>

          {/* Details Section */}
          <div className="flex flex-col items-center gap-5 text-center font-sans-dreamy flex-grow justify-center">
            
            <div className="w-full max-w-[360px] mx-auto">
              <div className="border-t border-b border-[#78350f]/30 py-4 flex items-center justify-center">
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-dreamy text-[7px] tracking-[0.32em] text-[#78350f]/60 uppercase block mb-1">DAY</span>
                  <span className="font-title-dreamy text-[9px] tracking-wider text-[#78350f] font-semibold block">{dayOfWeek}</span>
                  <span className="font-serif-dreamy text-[11.5px] text-stone-800 font-semibold block mt-0.5">{formattedDate}</span>
                </div>
                <div className="w-px h-10 bg-[#78350f]/20 mx-5 shrink-0" />
                <div className="flex-1 flex flex-col items-center">
                  <span className="font-title-dreamy text-[7px] tracking-[0.32em] text-[#78350f]/60 uppercase block mb-1">TIME</span>
                  <span className="font-title-dreamy text-[9px] tracking-wider text-[#78350f] font-semibold block">AT</span>
                  <span className="font-serif-dreamy text-[11.5px] text-stone-800 font-semibold block mt-0.5">{eventTime}</span>
                </div>
              </div>
            </div>

            <div className="w-full max-w-[340px] mx-auto text-center">
              <span className="font-title-dreamy text-[7px] tracking-[0.35em] text-[#78350f]/60 uppercase block mb-1.5">VENUE</span>
              <span className="font-title-dreamy text-[10px] tracking-[0.18em] text-[#78350f] font-semibold block uppercase">{venueName}</span>
              <span className="font-sans-dreamy text-[9px] text-stone-600 block leading-relaxed mt-1">{venueAddress}</span>
            </div>

            <div
              role="button"
              aria-label="RSVP Now"
              onClick={() => setShowRsvpModal(true)}
              className="cursor-pointer group"
            >
              <div
                className="px-9 py-2.5 rounded-full border border-[#78350f]/30 bg-gradient-to-r from-[#fffbeb] via-white to-[#fffbeb] group-hover:from-[#fef3c7] group-hover:to-[#fef3c7] transition-all duration-200"
                style={{ boxShadow: '0 2px 14px rgba(120,53,15,0.06)' }}
              >
                <span className="font-title-dreamy text-[8.5px] tracking-[0.38em] text-[#78350f] font-semibold uppercase block text-center">RSVP</span>
                <span className="font-sans-dreamy text-[9.5px] text-stone-700 font-medium block text-center mt-0.5">{rsvpName} · {rsvpPhone}</span>
              </div>
            </div>

            {dressCode && (
              <div
                className="inline-block px-5 py-1.5 rounded-full border border-[#78350f]/30"
                style={{ background: 'rgba(255,251,235,0.60)' }}
              >
                <span className="font-title-dreamy text-[7.5px] tracking-[0.28em] text-[#78350f] uppercase font-semibold">
                  DRESS CODE &ensp;·&ensp; {dressCode}
                </span>
              </div>
            )}
          </div>

          {/* Bottom Section */}
          <div className="text-center pt-4">
            <span className="font-serif-dreamy text-[10px] text-stone-600 block leading-relaxed max-w-[300px] mx-auto mb-3">
              "{message}"
            </span>

            <svg viewBox="0 0 180 10" className="w-36 h-2 mx-auto mb-3" fill="none">
              <line x1="0" y1="5" x2="72" y2="5" stroke="#78350f" strokeWidth="0.7" opacity="0.4"/>
              <circle cx="90" cy="5" r="2" fill="#78350f" opacity="0.5"/>
              <line x1="108" y1="5" x2="180" y2="5" stroke="#78350f" strokeWidth="0.7" opacity="0.4"/>
            </svg>

            <span className="font-serif-dreamy text-[11px] text-[#78350f]/80 block tracking-wide">
              Hosted with love by
            </span>
            <span className="font-title-dreamy text-[9.5px] tracking-[0.28em] text-stone-800 font-semibold block mt-1 uppercase">
              {hostedBy}
            </span>
          </div>

        </div>

      </div>

      {/* RSVP Modal Popup */}
      {showRsvpModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div 
            className="absolute inset-0 bg-stone-950/65 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => setShowRsvpModal(false)}
          />
          
          <div className="relative w-full max-w-md bg-stone-900 border border-white/10 rounded-3xl p-6 shadow-2xl z-10 overflow-hidden animate-in fade-in zoom-in-95 duration-200">
            <button
              type="button"
              onClick={() => setShowRsvpModal(false)}
              className="absolute top-4 right-4 text-white/50 hover:text-white transition-colors"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            
            <div className="text-center mb-6">
              <h3 className="text-2xl font-serif text-white font-semibold">Are You Attending?</h3>
              <p className="text-white/60 text-sm mt-1">Please RSVP to let us know if you're coming!</p>
            </div>
            
            <RsvpForm invitationId={invitationId} isLive={isLive} externalLink={data.rsvpLink || null} />
          </div>
        </div>
      )}
    </div>
  );
}

registry.register({ metadata: META, component: DreamyMoonGold });
